import type {
  PermissionRolesMap,
  FormattedPermissions,
} from '../../types/auth.types';

/**
 * INTERNAL_POST_PROCESSING (DV-2751) is an internal role WITHOUT blanket
 * access, so unlike INTERNAL_ADMIN — which short-circuits to allow-all in
 * util/permissions.ts and never reaches this table — it needs an explicit
 * column here. Without one it resolved to 'role-not-mapped' and was denied
 * everywhere.
 *
 * The grant follows the DV-2751 checklist: asset, property and order views,
 * and NOT Admin Console / account management. Concretely:
 *   - Assets      full CRUD — this is the role's actual job (guided captioning,
 *                 staging, edit suite). The destructive affordances are
 *                 additionally origin-gated by assetFeatureFlags, so they stay
 *                 dark in prod for internal users generally.
 *   - Properties  read-only — they browse properties to reach the assets.
 *   - Order       read-only — they need order context, not order control.
 *   - Entities    read-only — required so org switching can resolve.
 *   - External user  read-only — view parity with CUSTOMER_USER, which can see
 *                 the team list. GET only: POST/PUT/DELETE stay false, and
 *                 TeamMembers already renders off canPut/canPost, so the Add
 *                 button and the editable name cells never appear.
 *   - Cart / Integration / Notifications  no access; these are purchasing and
 *                 account-management surfaces. Cart is deliberately NOT given
 *                 read parity with CUSTOMER_USER: with POST/DELETE :Cart false
 *                 the page could never be filled or emptied, so it would only
 *                 ever render blank.
 *
 * A route is visible when ANY action on its resource is true (see
 * getPermissionForRoute), so the all-false resources above are hidden outright.
 *
 * DELETE :Assets is the one destructive permission in the set and was granted
 * explicitly (DV-2751): the edit suite this role exists to run includes
 * delete/trash, so withholding it would leave the role unable to do its job.
 * It remains additionally origin-gated by assetFeatureFlags, so it stays dark
 * in production like the rest of the edit suite.
 */
export const permissionRolesMap: PermissionRolesMap = {
  'POST :External user': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :Assets': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: true
  },
  'POST :Assets': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: true
  },
  'DELETE :Assets': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: true
  },
  'POST :Entities': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :Notifications': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: false,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :External user': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    // View parity with CUSTOMER_USER. Read-only: the POST/PUT/DELETE rows for
    // this resource stay false, and TeamMembers renders its Add button and
    // editable name cells off canPost/canPut, so neither appears.
    INTERNAL_POST_PROCESSING: true
  },
  'GET :Integration': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Integration': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'POST :Integration': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'DELETE :Integration': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Properties': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'DELETE :Entities': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Cart': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'POST :Properties': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Order': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'POST :Notifications': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Entities': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :Order': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: true
  },
  'DELETE :Order': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Notifications': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: false,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :Properties': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: true
  },
  'DELETE :Properties': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'DELETE :External user': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'POST :Cart': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'GET :Entities': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: true
  },
  'GET :Cart': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: true,
    CUSTOMER_DEVELOPER: true,
    INTERNAL_POST_PROCESSING: false
  },
  'DELETE :Notifications': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: false,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'DELETE :Cart': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'POST :Order': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :External user': {
    CUSTOMER_USER: false,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: false
  },
  'PUT :Assets': {
    CUSTOMER_USER: true,
    CUSTOMER_ADMIN: true,
    CUSTOMER_FINANCE: false,
    CUSTOMER_DEVELOPER: false,
    INTERNAL_POST_PROCESSING: true
  }
};

const formatPermissions = (roleMap: PermissionRolesMap): FormattedPermissions => {
  const out: FormattedPermissions = {};
  Object.entries(roleMap).forEach(([action, roles]) => {
    const [method, resource] = action.split(' :');
    Object.entries(roles).forEach(([role, hasPermission]) => {
      if (!out[role]) out[role] = {};
      if (!out[role][resource]) out[role][resource] = {};
      out[role][resource][`${method} :${resource}`] = hasPermission;
    });
  });
  return out;
};

/**
 * Frozen role → resource → action → boolean map computed once at import time.
 * Single runtime source of truth for RBAC lookups; replaces the prior
 * localStorage('permissions') roundtrip that could drift out of sync between
 * the in-memory state and the persisted copy.
 */
export const formattedPermissions: Readonly<FormattedPermissions> = Object.freeze(
  formatPermissions(permissionRolesMap),
);
